import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { LogoLockup, LogoSeal, Motif } from "../components/brand/Brand";
import { RoadDivider, RulerDivider, RidgeDivider, GrassDivider } from "../components/brand/Dividers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { ArrowRight, Check, Phone, LogIn, MapPin } from "lucide-react";

const FAQ = [
  ["Is this just a business audit?",
   "No. An audit tells you what is wrong. My work goes further: I look for why a problem is happening, whether it is worth solving, what should be done first, and a practical way forward. The point is a better running business, not a list of complaints."],
  ["Are you going to tell me how to do my trade?",
   "No. You know your trade far better than I ever will. My job is to help with the business that has grown up around the trade: how calls come in, how jobs get scheduled, how estimates and invoices happen, and where time and money quietly leak out."],
  ["What if I already know what my problems are?",
   "Good. That is a strong starting point. Owners are usually right about the symptoms. Often the useful question is why the problem keeps happening and what is actually holding the fix in place. That is where I spend my time."],
  ["What exactly do I get for $297?",
   "A private Business Assessment, my review of how your business really runs, independent investigation and research, a clear read on your constraints and your strengths, prioritized recommendations, and a customized finished Long Range Blueprint you can act on."],
  ["Do I have to hire Long Range to implement the recommendations?",
   "No, and you never will. The Blueprint has value on its own. You can implement it yourself, hand it to someone you already trust, hire Long Range where it makes sense, or decide a recommendation is not worth it. The recommendation comes first. Whether I make additional money implementing it comes second."],
  ["How long does the process take?",
   "The typical target is about one week after I receive your completed Business Assessment."],
  ["How much time do you need from me?",
   "Mainly the Business Assessment. It is intentionally thorough because useful recommendations require more than a surface look. Your progress saves as you go, and you can stop and return later."],
  ["What information do I have to share?",
   "How your business actually operates day to day, in your own words. You will not be asked for tax returns or anything you are not comfortable sharing."],
  ["Are you figuring all of this out from a questionnaire and Google?",
   "The assessment is where you tell me how things really work. From there I investigate independently, research relevant issues and possible solutions, and test assumptions rather than repeating back what you already told me."],
  ["What if something in my business already works extremely well?",
   "Then I say so, and I recommend leaving it alone. A working referral channel or a proven habit does not need to be replaced just because a newer option exists. Part of the job is protecting what already works."],
  ["What happens after I receive my Blueprint?",
   "It is yours. You decide what to act on and when. If you want help with any part of it, we can talk about that separately. There is no obligation."],
  ["Is Long Range new?",
   "Yes. Long Range is new and I am introducing it to businesses here in Cochise County, which is why the introductory rate is $297. The care and depth behind each Blueprint are not new to me."],
];

function DragoonRidge() {
  return (
    <svg viewBox="0 0 240 44" width="200" height="37" aria-hidden="true" className="mx-auto opacity-80" data-testid="dragoon-ridge">
      <path d="M2 40 L34 24 L52 30 L78 12 L96 22 L120 6 L146 22 L168 13 L192 27 L214 20 L238 34"
        fill="none" stroke="#5A6047" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 42 L34 30 L52 34 L78 22 L96 29 L120 18 L146 30 L168 24 L192 33 L214 29 L238 40"
        fill="none" stroke="#A65F42" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
      <circle cx="120" cy="6" r="2" fill="#A65F42" />
    </svg>
  );
}

function Section({ id, children, className = "" }) {
  return <section id={id} className={`w-full ${className}`}><div className="max-w-5xl mx-auto px-6">{children}</div></section>;
}

function BlueprintFinding({ tag, tagColor, title, rows }) {
  return (
    <div className="lr-card p-5">
      <span className={`inline-flex px-2 py-0.5 rounded-[3px] text-xs font-semibold tracking-wide ${tagColor}`}>{tag}</span>
      <h4 className="font-serif text-lg text-lr-charcoal mt-3">{title}</h4>
      <dl className="mt-3 space-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs uppercase tracking-wide text-lr-clay">{k}</dt>
            <dd className="text-sm text-lr-charcoal leading-relaxed">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [starting, setStarting] = useState(false);
  const [photoOk, setPhotoOk] = useState(true);
  const [form, setForm] = useState({ preferred_contact: "email" });
  const [sending, setSending] = useState(false);
  const contactRef = useRef(null);

  const startAssessment = async () => {
    setStarting(true);
    try {
      // Returning clients who already paid resume their assessment directly
      const existing = localStorage.getItem("lr_intake_token");
      if (existing) {
        try {
          await api.get(`/public/intake/${existing}`);
          navigate(`/assessment/${existing}`);
          return;
        } catch (e) {
          localStorage.removeItem("lr_intake_token");
        }
      }
      // New clients pay the $297 Blueprint up front, then enter the assessment
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStarting(false);
    }
  };

  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });

  const sendContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please include your name and email."); return; }
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thank you. Your message reached Alison and she will be in touch.");
      setForm({ preferred_contact: "email" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const fade = (d = 0) => (reduce ? {} : { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, delay: d } });

  const StartBtn = ({ full }) => (
    <Button onClick={startAssessment} disabled={starting} data-testid="start-assessment-btn"
      className={`relative h-14 px-9 sm:px-14 text-sm sm:text-base bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px] group ${full ? "w-full sm:w-auto" : ""}`}>
      <span className="block w-full text-center whitespace-nowrap">{starting ? "Starting…" : "Start Your Business Assessment"}</span>
      <ArrowRight className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-0.5" />
    </Button>
  );
  const TalkBtn = ({ full }) => (
    <Button onClick={scrollToContact} variant="outline" data-testid="talk-to-alison-btn"
      className={`h-14 px-8 text-base rounded-[4px] border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10 ${full ? "w-full sm:w-auto" : ""}`}>
      Talk to Me First
    </Button>
  );

  return (
    <div className="min-h-screen w-full flex flex-col paper-grain">
      <header className="max-w-5xl w-full mx-auto px-6 pt-6 sm:pt-8">
        <LogoLockup className="h-20 sm:h-20" />
      </header>

      {/* HERO */}
      <div className="relative w-full overflow-hidden">
        <div className="absolute inset-0 -z-10" aria-hidden="true">
          <img src="/brand/dragoon-hero.jpg" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(243,235,221,0.78) 0%, rgba(243,235,221,0.62) 32%, rgba(243,235,221,0.66) 52%, rgba(243,235,221,0.86) 78%, #F3EBDD 100%)" }} />
        </div>
        <div className="max-w-5xl mx-auto px-6 pt-8 sm:pt-12 pb-16">
          <motion.p {...fade(0)} className="font-sans text-xs sm:text-sm tracking-[0.28em] text-lr-clay mb-4">
            COCHISE COUNTY, ARIZONA
          </motion.p>
          <motion.h1 {...fade(0.05)} className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-lr-charcoal max-w-3xl">
            A capable set of eyes on the business behind your trade.
          </motion.h1>
          <motion.p {...fade(0.12)} className="mt-6 text-base sm:text-lg text-lr-charcoal/90 max-w-2xl leading-relaxed">
            Small business owners hire me to dig into how their business is running, figure out what is eating up their time or money, what is causing headaches, what is getting missed, and find practical, innovative ways to fix those things.
          </motion.p>
          <motion.div {...fade(0.2)} className="mt-8 flex flex-col sm:flex-row gap-3">
            <StartBtn full /><TalkBtn full />
          </motion.div>
          <motion.p {...fade(0.28)} className="mt-4 text-sm text-lr-charcoal/80">
            The Long Range Blueprint. $297 introductory Cochise County rate.
          </motion.p>
        </div>
      </div>

      {/* WHAT I ACTUALLY DO */}
      <Section className="py-14 bg-lr-card/50 border-y hairline">
        <motion.div {...fade()}>
          <h2 className="font-serif text-3xl text-lr-charcoal">What I actually do</h2>
          <p className="mt-5 text-lg text-lr-charcoal/90 leading-relaxed max-w-3xl">
            I am not here to tell you how to do your trade. My job is to help with the business that has grown up around the trade. I work from your side of the table, looking at how the work really moves through your company and where the day to day quietly costs you.
          </p>
          <blockquote className="mt-6 border-l-2 border-lr-clay pl-5 font-serif text-xl text-lr-charcoal max-w-3xl">
            I don't want to run your business. I want to help make running your business easier.
          </blockquote>
        </motion.div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="py-16">
        <RoadDivider />
        <motion.h2 {...fade()} className="font-serif text-3xl text-lr-charcoal mb-8">How it works</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            ["01", "Tell me how the business actually works", "You complete the private Long Range Business Assessment. It is intentionally thorough, because useful recommendations require more than a surface look at a business. Your progress saves as you go, and you can stop and return later."],
            ["02", "I investigate", "I review what you have told me, look at the business from the inside out, research relevant issues and possible solutions independently, test assumptions, and separate symptoms from the underlying problems."],
            ["03", "Receive your Blueprint", "You receive a customized, prioritized Blueprint explaining what I found, what I recommend, what should happen first, and what should be left alone."],
          ].map(([n, t, d], i) => (
            <motion.div key={n} {...fade(i * 0.08)} className="lr-card p-6">
              <div className="font-serif text-3xl text-lr-clay">{n}</div>
              <h3 className="font-serif text-xl text-lr-charcoal mt-2">{t}</h3>
              <p className="mt-3 text-sm text-lr-charcoal/85 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SAMPLE BLUEPRINT */}
      <Section className="py-16 bg-lr-card/50 border-y hairline">
        <RulerDivider />
        <motion.div {...fade()} className="flex items-center gap-3 flex-wrap">
          <h2 className="font-serif text-3xl text-lr-charcoal">What a Blueprint actually contains</h2>
          <span className="inline-flex px-2.5 py-1 rounded-[3px] text-xs font-semibold tracking-wide bg-lr-clay/15 text-lr-clay" data-testid="fictional-label">
            FICTIONAL EXAMPLE
          </span>
        </motion.div>
        <motion.p {...fade(0.05)} className="mt-3 text-lr-charcoal/85 max-w-3xl">
          This is an invented example for a fictional local plumbing business. It does not represent a real client. It is here to show the depth and specificity of what you receive.
        </motion.p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <BlueprintFinding tag="PRIORITY: FIRST" tagColor="bg-lr-olive/15 text-lr-olive"
            title="Estimates are taking days to go out"
            rows={[
              ["Finding", "Estimates take several days because routine pricing decisions keep routing back through the owner."],
              ["Reasoning", "Most delayed estimates were for common, repeatable work, yet each one waited on the owner for a price."],
              ["Underlying issue", "The bottleneck is likely not the estimating software. Common work lacks standardized pricing, and too many routine estimates require owner approval."],
              ["Recommended solution", "Build a standardized pricing library for common jobs and define which estimates can be sent without owner review."],
              ["Expected benefit", "Faster estimates, fewer interruptions for the owner, and less chance of good jobs going cold while a customer waits."],
              ["Implementation direction", "Start with the ten most common job types. This can be done inside tools the company already uses."],
            ]} />
          <BlueprintFinding tag="PRIORITY: NEXT" tagColor="bg-lr-clay/15 text-lr-clay"
            title="Invoices are handwritten"
            rows={[
              ["Finding", "Invoices are written by hand and totals are re-entered later when payments are recorded."],
              ["Reasoning", "The same job details are captured on paper, then keyed again elsewhere, and a few invoices went uncollected."],
              ["Underlying issue", "The question is not simply which software to buy. It is how invoices are created, what they contain, how customers receive them, how payment is tracked, and what the company already uses."],
              ["Recommended solution", "Choose an approach that fits the current workflow and existing tools, rather than adding a system that duplicates effort. The fit matters more than the brand."],
              ["Expected benefit", "Less double entry, cleaner records, and fewer invoices slipping through the cracks."],
              ["Implementation direction", "Review current tools first, then select the simplest option that removes the re-entry step."],
            ]} />
          <BlueprintFinding tag="LEAVE ALONE" tagColor="bg-lr-leather/15 text-lr-leather"
            title="Word of mouth referrals"
            rows={[
              ["Finding", "Word of mouth referrals are producing profitable, well matched customers."],
              ["Recommendation", "Leave this alone. Do not replace a working referral channel just because newer marketing options exist. Protect it."],
            ]} />
          <div className="lr-card p-5 flex flex-col justify-center">
            <p className="font-serif text-xl text-lr-charcoal">Every finding follows the same honest shape.</p>
            <ul className="mt-4 space-y-2 text-sm text-lr-charcoal/85">
              {["Finding", "Evidence or reasoning", "Underlying issue", "Recommended solution", "Priority", "Expected benefit", "Implementation direction", "Leave Alone where it applies"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-lr-olive flex-shrink-0" /> {x}</li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-lr-leather">No invented savings figures. No guaranteed outcomes. Just clear, practical direction.</p>
          </div>
        </div>
      </Section>

      {/* PRICING */}
      <Section className="py-16">
        <RidgeDivider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <motion.div {...fade()}>
            <h2 className="font-serif text-3xl text-lr-charcoal">Simple pricing</h2>
            <div className="mt-5 lr-card p-6">
              <div className="font-serif text-xl text-lr-charcoal">Long Range Blueprint</div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-serif text-5xl text-lr-olive">$297</span>
                <span className="text-sm text-lr-leather">introductory Cochise County rate</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-lr-charcoal/90">
                {["Private Business Assessment", "Founder review of how your business runs", "Independent investigation and research", "Identification of constraints, strengths, and opportunities", "Research into potential solutions", "Prioritization of what to do first", "Customized finished Long Range Blueprint"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><Check className="h-4 w-4 text-lr-olive mt-0.5 flex-shrink-0" /> {x}</li>
                ))}
              </ul>
              <div className="mt-6"><StartBtn full /></div>
            </div>
          </motion.div>
          <motion.div {...fade(0.08)} className="lr-card p-6">
            <h3 className="font-serif text-xl text-lr-charcoal">Implementation is separate and optional</h3>
            <p className="mt-3 text-sm text-lr-charcoal/85 leading-relaxed">
              The $297 pays for the Blueprint itself, and the Blueprint stands on its own whether you ever hire me again or not. Where it makes sense, I can also help carry out the work: marketing, website work, software setup, automation, workflow design, and business systems. That help is never required and never included in the price.
            </p>
            <p className="mt-4 text-sm text-lr-charcoal/85 leading-relaxed">
              You can implement recommendations yourself, hire someone you already trust, hire Long Range where it fits, or decide not to act on a recommendation at all. The recommendation comes first. Whether I make additional money implementing it comes second.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* WHY DIFFERENT */}
      <Section className="py-16 bg-lr-card/50 border-y hairline">
        <motion.div {...fade()}>
          <h2 className="font-serif text-3xl text-lr-charcoal">Why Long Range is different</h2>
          <p className="mt-5 text-lg text-lr-charcoal/90 leading-relaxed max-w-3xl">
            Specialists naturally look at a business through their own area. A marketer examines marketing. A web designer examines the website. An accountant examines the finances. A software provider examines software. All of them can be genuinely valuable.
          </p>
          <p className="mt-4 text-lg text-lr-charcoal/90 leading-relaxed max-w-3xl">
            Long Range starts one step earlier, with a plainer question: what problem is actually worth solving? The solution should follow the diagnosis, not the other way around.
          </p>
        </motion.div>
      </Section>

      {/* ABOUT ALISON */}
      <Section className="py-16">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 items-start">
          <motion.div {...fade()} className="lr-card overflow-hidden">
            {photoOk ? (
              <img src="/brand/alison.jpg" alt="Alison, founder of Long Range" onError={() => setPhotoOk(false)}
                className="w-full h-auto object-cover" data-testid="alison-photo" />
            ) : (
              <div className="aspect-[4/5] flex items-center justify-center bg-lr-cream" data-testid="alison-photo-placeholder">
                <LogoSeal size={140} />
              </div>
            )}
          </motion.div>
          <motion.div {...fade(0.08)}>
            <h2 className="font-serif text-3xl text-lr-charcoal">About Alison</h2>
            <p className="mt-5 text-lr-charcoal/90 leading-relaxed">
              I have always been entrepreneurial at heart, with a genuine pull toward businesses, systems, research, and problem solving. Life also led me through significant years raising a large family and working from home. That never removed the instinct. If anything, it sharpened it.
            </p>
            <p className="mt-4 text-lr-charcoal/90 leading-relaxed">
              I am the kind of person who sees a problem in how something is being done and immediately wants to understand why it is happening, what the alternatives are, and how it could work better. Long Range is me turning that instinct into the work itself, for the owner-operated businesses that keep this county running.
            </p>
            <blockquote className="mt-6 border-l-2 border-lr-clay pl-5 font-serif text-xl text-lr-charcoal">
              I am not attached to a particular answer. I am attached to finding the one that makes sense for your business.
            </blockquote>
          </motion.div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16 bg-lr-card/50 border-y hairline">
        <GrassDivider />
        <motion.h2 {...fade()} className="font-serif text-3xl text-lr-charcoal mb-6">Questions owners actually ask</motion.h2>
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b hairline">
              <AccordionTrigger className="text-left font-serif text-lg text-lr-charcoal hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-lr-charcoal/85 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-16">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto">
          <DragoonRidge />
          <p className="mt-6 font-serif text-2xl sm:text-3xl text-lr-charcoal leading-snug">
            You know your trade. You know your customers. You know your business. Sometimes what you need is another capable set of eyes with the time to step outside the day to day and work on the problems you have not had time to solve.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <StartBtn /><TalkBtn />
          </div>
        </motion.div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="py-16 bg-lr-card/50 border-y hairline">
        <div ref={contactRef} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div {...fade()}>
            <h2 className="font-serif text-3xl text-lr-charcoal">Talk to Alison first</h2>
            <p className="mt-4 text-lr-charcoal/85 leading-relaxed">
              If you would rather talk before you start, send a short note and tell me the best way to reach you. There is no pressure and no obligation.
            </p>
            <p className="mt-4 text-sm text-lr-leather flex items-center gap-2"><MapPin className="h-4 w-4" /> Serving Cochise County, Arizona.</p>
          </motion.div>
          <motion.form {...fade(0.08)} onSubmit={sendContact} className="lr-card p-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-lr-charcoal">Your name</Label>
                <Input className="mt-1 bg-white/60" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" required /></div>
              <div><Label className="text-lr-charcoal">Business name</Label>
                <Input className="mt-1 bg-white/60" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="contact-business" /></div>
              <div><Label className="text-lr-charcoal">Email</Label>
                <Input type="email" className="mt-1 bg-white/60" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" required /></div>
              <div><Label className="text-lr-charcoal">Phone</Label>
                <Input className="mt-1 bg-white/60" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></div>
            </div>
            <div><Label className="text-lr-charcoal">Preferred contact method</Label>
              <Select value={form.preferred_contact} onValueChange={(v) => setForm({ ...form, preferred_contact: v })}>
                <SelectTrigger className="mt-1 bg-white/60" data-testid="contact-preferred"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="text">Text message</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-lr-charcoal">Message</Label>
              <Textarea rows={4} className="mt-1 bg-white/60" value={form.message || ""} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" placeholder="Tell me a little about your business, or what you are wondering about." /></div>
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full h-12 bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
              {sending ? "Sending…" : "Send to Alison"}
            </Button>
          </motion.form>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="max-w-5xl w-full mx-auto px-6 pb-10 pt-8">
        <div className="border-t hairline pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoLockup className="h-10" />
          <div className="flex items-center gap-6 text-sm">
            <button onClick={startAssessment} className="text-lr-olive font-medium hover:text-lr-clay transition-colors">Start Assessment</button>
            <Link to="/login" data-testid="founder-login-link"
              className="inline-flex items-center gap-1.5 text-lr-leather hover:text-lr-olive transition-colors">
              <LogIn className="h-4 w-4" /> Founder sign in
            </Link>
          </div>
        </div>
        <p className="mt-4 text-xs text-lr-leather/70 text-center sm:text-left">Long Range, Strategic Business Systems. Cochise County, Arizona.</p>
      </footer>
    </div>
  );
}
